import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  UserCircleIcon,
  MapPinIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  XMarkIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  getPipeline,
  moveCandidateInPipeline,
  addPipelineNote,
  getPipelineNotes,
  getPipelineHistory
} from '../services/api';
import { useRealtimeSubscription } from '../hooks/useRealtimeSubscription';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import CandidateJobsPanel from '../components/CandidateJobsPanel';
import { FEATURES } from '../config/features';

// Stage configuration with colors and labels
const STAGE_CONFIG = {
  new: { label: 'New', color: 'bg-gray-100', textColor: 'text-gray-700', borderColor: 'border-gray-300' },
  contacted: { label: 'Contacted', color: 'bg-blue-50', textColor: 'text-blue-700', borderColor: 'border-blue-300' },
  replied: { label: 'Replied', color: 'bg-indigo-50', textColor: 'text-indigo-700', borderColor: 'border-indigo-300' },
  interviewing: { label: 'Interviewing', color: 'bg-yellow-50', textColor: 'text-yellow-700', borderColor: 'border-yellow-300' },
  offer: { label: 'Offer', color: 'bg-purple-50', textColor: 'text-purple-700', borderColor: 'border-purple-300' },
  hired: { label: 'Hired', color: 'bg-green-50', textColor: 'text-green-700', borderColor: 'border-green-300' },
  rejected: { label: 'Rejected', color: 'bg-red-50', textColor: 'text-red-700', borderColor: 'border-red-300' }
};

const STAGES = ['new', 'contacted', 'replied', 'interviewing', 'offer', 'hired', 'rejected'];

// Match score badge component
const MatchScoreBadge = ({ score }) => {
  let bgColor = 'bg-gray-100 text-gray-600';
  if (score >= 80) bgColor = 'bg-green-100 text-green-700';
  else if (score >= 60) bgColor = 'bg-yellow-100 text-yellow-700';
  else if (score >= 40) bgColor = 'bg-orange-100 text-orange-700';
  else if (score > 0) bgColor = 'bg-red-100 text-red-700';

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${bgColor}`}>
      {score}%
    </span>
  );
};

// Candidate card component
const CandidateCard = ({ candidate, index, onOpenNotes }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'No activity';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Draggable draggableId={candidate.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`bg-white rounded-lg shadow-sm border p-3 mb-2 cursor-grab active:cursor-grabbing transition-shadow ${
            snapshot.isDragging ? 'shadow-lg ring-2 ring-indigo-300' : 'hover:shadow-md'
          }`}
        >
          {/* Header: Name and Match Score */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <UserCircleIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
              <div className="min-w-0">
                <h4 className="font-medium text-gray-900 text-sm truncate">{candidate.fullName}</h4>
                <p className="text-xs text-gray-500 truncate">{candidate.desiredRoles || candidate.domain}</p>
              </div>
            </div>
            <MatchScoreBadge score={candidate.matchScore} />
          </div>

          {/* Domain badge */}
          {candidate.domain && (
            <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded mb-2">
              {candidate.domain}
            </span>
          )}

          {/* Skills preview */}
          {candidate.skills && candidate.skills.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {candidate.skills.slice(0, 3).map((skill, i) => (
                <span key={i} className="bg-indigo-50 text-indigo-600 text-xs px-1.5 py-0.5 rounded">
                  {skill}
                </span>
              ))}
              {candidate.skills.length > 3 && (
                <span className="text-xs text-gray-400">+{candidate.skills.length - 3}</span>
              )}
            </div>
          )}

          {/* Footer: Location and Activity */}
          <div className="flex items-center justify-between text-xs text-gray-500 mt-2 pt-2 border-t">
            <div className="flex items-center gap-1">
              {candidate.location && (
                <>
                  <MapPinIcon className="h-3 w-3" />
                  <span className="truncate max-w-[80px]">{candidate.location}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <ClockIcon className="h-3 w-3" />
                <span>{formatDate(candidate.lastActivity)}</span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenNotes(candidate);
                }}
                className="p-1 hover:bg-gray-100 rounded"
                title="Add note"
              >
                <ChatBubbleLeftIcon className="h-4 w-4 text-gray-400 hover:text-indigo-600" />
              </button>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};

// Stage column component
const StageColumn = ({ stage, candidates, onOpenNotes }) => {
  const config = STAGE_CONFIG[stage];

  return (
    <div className={`flex-shrink-0 w-72 ${config.color} rounded-lg border ${config.borderColor}`}>
      {/* Column header */}
      <div className={`p-3 border-b ${config.borderColor}`}>
        <div className="flex items-center justify-between">
          <h3 className={`font-semibold ${config.textColor}`}>{config.label}</h3>
          <span className={`${config.textColor} bg-white px-2 py-0.5 rounded-full text-sm font-medium`}>
            {candidates.length}
          </span>
        </div>
      </div>

      {/* Droppable area */}
      <Droppable droppableId={stage}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`p-2 min-h-[400px] max-h-[calc(100vh-280px)] overflow-y-auto ${
              snapshot.isDraggingOver ? 'bg-indigo-50' : ''
            }`}
          >
            {candidates.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No candidates
              </div>
            ) : (
              candidates.map((candidate, index) => (
                <CandidateCard
                  key={candidate.id}
                  candidate={candidate}
                  index={index}
                  onOpenNotes={onOpenNotes}
                />
              ))
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

// Notes slide-over component
const NotesSlideOver = ({ candidate, isOpen, onClose }) => {
  const [notes, setNotes] = useState([]);
  const [history, setHistory] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('notes');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [notesRes, historyRes] = await Promise.all([
        getPipelineNotes(candidate.id),
        getPipelineHistory(candidate.id)
      ]);
      setNotes(notesRes.data.notes || []);
      setHistory(historyRes.data.history || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [candidate]);

  // Realtime: refresh notes when comments are added/updated for this candidate
  useRealtimeSubscription({
    table: 'comments',
    event: 'INSERT',
    filter: candidate ? `candidate_id=eq.${candidate.id}` : undefined,
    enabled: isOpen && !!candidate,
    onInsert: () => {
      // Refresh notes when a collaborator adds a comment
      fetchData();
    },
  });

  useEffect(() => {
    if (isOpen && candidate) {
      fetchData();
    }
  }, [isOpen, candidate, fetchData]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    setSubmitting(true);
    try {
      await addPipelineNote(candidate.id, newNote.trim());
      setNewNote('');
      toast.success('Note added');
      fetchData();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-50" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 max-w-md w-full bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{candidate?.fullName}</h3>
            <p className="text-sm text-gray-500">{candidate?.domain}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('notes')}
            className={`flex-1 py-3 text-sm font-medium ${
              activeTab === 'notes'
                ? 'text-indigo-600 border-b-2 border-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Notes
          </button>
          {FEATURES.multiJob && (
            <button
              onClick={() => setActiveTab('jobs')}
              className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-1 ${
                activeTab === 'jobs'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <BriefcaseIcon className="h-4 w-4" />
              Jobs
            </button>
          )}
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-3 text-sm font-medium ${
              activeTab === 'history'
                ? 'text-indigo-600 border-b-2 border-indigo-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            History
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <ArrowPathIcon className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : activeTab === 'notes' ? (
            <>
              {/* Add note form */}
              <form onSubmit={handleAddNote} className="mb-4">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  rows={3}
                />
                <button
                  type="submit"
                  disabled={submitting || !newNote.trim()}
                  className="mt-2 w-full bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                >
                  {submitting ? 'Adding...' : 'Add Note'}
                </button>
              </form>

              {/* Notes list */}
              {notes.length === 0 ? (
                <p className="text-center text-gray-400 py-4">No notes yet</p>
              ) : (
                <div className="space-y-3">
                  {notes.map((note, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-700">{note.content}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {note.createdBy?.name || 'Unknown'} - {formatDate(note.createdAt)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (FEATURES.multiJob && activeTab === 'jobs') ? (
            /* Jobs Panel (deferred sourcing surface) */
            <CandidateJobsPanel
              candidateId={candidate?.id}
              candidateName={candidate?.fullName}
            />
          ) : (
            /* History list */
            history.length === 0 ? (
              <p className="text-center text-gray-400 py-4">No history yet</p>
            ) : (
              <div className="space-y-3">
                {history.map((entry, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-700">
                        Moved from <span className="font-medium">{STAGE_CONFIG[entry.fromStage]?.label || entry.fromStage}</span> to{' '}
                        <span className="font-medium">{STAGE_CONFIG[entry.toStage]?.label || entry.toStage}</span>
                      </p>
                      {entry.note && (
                        <p className="text-sm text-gray-500 italic mt-1">"{entry.note}"</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {entry.movedBy?.name || 'System'} - {formatDate(entry.movedAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

// Main Pipeline component
function Pipeline() {
  const [pipeline, setPipeline] = useState({});
  const [stageCounts, setStageCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const { user, isAuthenticated } = useSupabaseAuth();

  // Realtime: refresh pipeline when candidate_submissions are updated (stage changes)
  useRealtimeSubscription({
    table: 'candidate_submissions',
    event: 'UPDATE',
    filter: user ? `user_id=eq.${user.id}` : undefined,
    enabled: isAuthenticated && !!user,
    onUpdate: () => {
      // Another user (collaborator) moved a candidate - refresh pipeline
      fetchPipelineQuiet();
    },
  });

  // Quiet refresh that does not show loading spinner (avoids flicker during drag)
  const fetchPipelineQuiet = useCallback(async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      const response = await getPipeline(params);
      setPipeline(response.data.pipeline || {});
      setStageCounts(response.data.stageCounts || {});
    } catch (err) {
      // Silently ignore - background refresh
      console.error('Background pipeline refresh failed:', err);
    }
  }, [searchQuery]);

  const fetchPipeline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;

      const response = await getPipeline(params);
      setPipeline(response.data.pipeline || {});
      setStageCounts(response.data.stageCounts || {});
    } catch (err) {
      console.error('Error fetching pipeline:', err);
      setError('Failed to load pipeline data');
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // Dropped outside a droppable area
    if (!destination) return;

    // Dropped in the same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const sourceStage = source.droppableId;
    const destStage = destination.droppableId;

    // Optimistically update UI
    const newPipeline = { ...pipeline };
    const sourceItems = [...(newPipeline[sourceStage] || [])];
    const destItems = sourceStage === destStage ? sourceItems : [...(newPipeline[destStage] || [])];

    const [movedItem] = sourceItems.splice(source.index, 1);
    destItems.splice(destination.index, 0, movedItem);

    newPipeline[sourceStage] = sourceItems;
    if (sourceStage !== destStage) {
      newPipeline[destStage] = destItems;
    }

    setPipeline(newPipeline);

    // Update counts
    if (sourceStage !== destStage) {
      setStageCounts(prev => ({
        ...prev,
        [sourceStage]: (prev[sourceStage] || 0) - 1,
        [destStage]: (prev[destStage] || 0) + 1
      }));
    }

    // Call API if stage changed
    if (sourceStage !== destStage) {
      try {
        await moveCandidateInPipeline(draggableId, destStage);
        toast.success(`Moved to ${STAGE_CONFIG[destStage].label}`);
      } catch (err) {
        console.error('Error moving candidate:', err);
        toast.error('Failed to move candidate');
        // Revert on error
        fetchPipeline();
      }
    }
  };

  const handleOpenNotes = (candidate) => {
    setSelectedCandidate(candidate);
    setIsNotesOpen(true);
  };

  const handleCloseNotes = () => {
    setIsNotesOpen(false);
    setSelectedCandidate(null);
  };

  return (
    <div className="h-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Candidate Pipeline</h1>
        <p className="text-gray-500 mt-1">Drag and drop candidates between stages to update their status</p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-4 mb-6">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search candidates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Refresh */}
        <button
          onClick={fetchPipeline}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Pipeline Stats */}
      <div className="flex items-center gap-4 mb-4 text-sm">
        <span className="text-gray-500">
          Total: <span className="font-medium text-gray-900">{Object.values(stageCounts).reduce((a, b) => a + b, 0)}</span> candidates
        </span>
        <span className="text-gray-300">|</span>
        <span className="text-green-600 flex items-center gap-1">
          <CheckCircleIcon className="h-4 w-4" />
          Hired: {stageCounts.hired || 0}
        </span>
        <span className="text-yellow-600 flex items-center gap-1">
          <ClockIcon className="h-4 w-4" />
          In Progress: {(stageCounts.contacted || 0) + (stageCounts.replied || 0) + (stageCounts.interviewing || 0) + (stageCounts.offer || 0)}
        </span>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-2">
          <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
          <span className="text-red-700">{error}</span>
          <button onClick={fetchPipeline} className="ml-auto text-red-600 hover:text-red-800 text-sm font-medium">
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && Object.keys(pipeline).length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-indigo-600" />
          <span className="ml-3 text-gray-500">Loading pipeline...</span>
        </div>
      ) : (
        /* Kanban Board */
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {STAGES.map(stage => (
              <StageColumn
                key={stage}
                stage={stage}
                candidates={pipeline[stage] || []}
                onOpenNotes={handleOpenNotes}
              />
            ))}
          </div>
        </DragDropContext>
      )}

      {/* Notes Slide-over */}
      <NotesSlideOver
        candidate={selectedCandidate}
        isOpen={isNotesOpen}
        onClose={handleCloseNotes}
      />
    </div>
  );
}

export default Pipeline;
